/**
 * @file free-quantity.ts
 * @module app/modules/orders-delivery/utils/free-quantity
 * @description Buy X Get Y free-quantity helpers and role visibility rules for order screens.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 2.1.2
 * @version 2.1.2
 */

import {
  Admin,
  FinanceManager,
  LoadingBayManager,
} from '../../../../utils/constants';

export interface BuyXGetYOfferSnapshot {
  buyQty?: number;
  freeQty?: number;
  validFrom?: string;
  validTo?: string;
  isRepeatable?: boolean;
  active?: boolean | string;
}

export interface QuantityTypeWithOffer {
  buyXGetYEnabled?: boolean;
  buyXGetY?: BuyXGetYOfferSnapshot;
  buyXGetYOffers?: BuyXGetYOfferSnapshot[];
}

interface UserForFreeQuantity {
  userType?: number;
  role?: { type?: number | string }[];
  roleAndPermission?: { isWarehouseManagerRole?: boolean; name?: string };
}

/** An offer day follows this timezone. Mirrors BUSINESS_TIME_ZONE in backend PromotionUtil. */
const BUSINESS_TIME_ZONE = 'Africa/Dar_es_Salaam';

const businessDayFormat = new Intl.DateTimeFormat('en-GB', {
  timeZone: BUSINESS_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

/** Today's calendar day (YYYY-MM-DD) in the business timezone. */
export const getBusinessDay = (atDate: Date = new Date()): string => {
  const parts = businessDayFormat.formatToParts(atDate);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((part) => part.type === type)?.value;
  return `${get('year')}-${get('month')}-${get('day')}`;
};

/** Offer dates are calendar days stored in UTC, so the UTC date is the day the admin chose, in every browser. */
const getOfferCalendarDay = (value: string): string | undefined => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date.toISOString().slice(0, 10);
};

/** Shows an offer date as DD/MM/YYYY without shifting it into the browser timezone. */
export const formatOfferDate = (value?: string): string => {
  const day = value ? getOfferCalendarDay(value) : undefined;
  return day ? day.split('-').reverse().join('/') : '';
};

/** Mirrors backend PromotionUtil.getActiveBuyXGetYOffers date check. */
const isOfferWithinDates = (offer: BuyXGetYOfferSnapshot, atDate: Date): boolean => {
  const today = getBusinessDay(atDate);
  const from = offer.validFrom ? getOfferCalendarDay(offer.validFrom) : null;
  const to = offer.validTo ? getOfferCalendarDay(offer.validTo) : null;
  return from !== undefined && to !== undefined &&
    (from === null || today >= from) &&
    (to === null || today <= to);
};

/** Mirrors backend PromotionUtil.getOffers: legacy inactive offers never apply. */
const getOffers = (quantityType?: QuantityTypeWithOffer): BuyXGetYOfferSnapshot[] => {
  const offers = quantityType?.buyXGetYOffers?.length
    ? quantityType.buyXGetYOffers
    : quantityType?.buyXGetY ? [quantityType.buyXGetY] : [];
  return offers.filter((offer) => offer?.active !== false && offer?.active !== 'false');
};

/** Mirrors backend PromotionUtil.isBuyXGetYActive. */
export const isBuyXGetYActive = (
  quantityType?: QuantityTypeWithOffer,
  atDate: Date = new Date()
): boolean => {
  if (!quantityType?.buyXGetYEnabled) return false;
  const offers = getOffers(quantityType);
  return offers.some((offer) => {
    if (!offer || !Number.isSafeInteger(Number(offer.buyQty)) ||
      !Number.isSafeInteger(Number(offer.freeQty)) || Number(offer.buyQty) < 1 || Number(offer.freeQty) < 1) return false;
    return isOfferWithinDates(offer, atDate);
  });
};

/** Mirrors backend PromotionUtil.getFreeQuantity; the server value on the saved order is authoritative. */
export const getFreeQuantity = (
  quantityType: QuantityTypeWithOffer | undefined,
  paidQty: number | string,
  atDate: Date = new Date(),
  ignoreValidity = false
): number => {
  const qty = Math.floor(Number(paidQty) || 0);
  if (qty <= 0 || !quantityType?.buyXGetYEnabled) return 0;
  if (!ignoreValidity && !isBuyXGetYActive(quantityType, atDate)) return 0;
  const offers = getOffers(quantityType);
  return offers.reduce((best, offer) => {
    const buyQty = Number(offer?.buyQty);
    const freeQty = Number(offer?.freeQty);
    if (!Number.isSafeInteger(buyQty) || !Number.isSafeInteger(freeQty) ||
      buyQty < 1 || freeQty < 1) return best;
    if (!ignoreValidity && !isOfferWithinDates(offer, atDate)) return best;
    const free = offer.isRepeatable === false
      ? (qty >= buyQty ? freeQty : 0)
      : Math.floor(qty / buyQty) * freeQty;
    return Math.max(best, free);
  }, 0);
};

export const getApplicableOffer = (
  quantityType: QuantityTypeWithOffer | undefined,
  paidQty: number | string,
  atDate: Date = new Date(),
  ignoreValidity = false
): BuyXGetYOfferSnapshot | undefined => {
  const qty = Math.floor(Number(paidQty) || 0);
  if (qty <= 0 || !quantityType?.buyXGetYEnabled) return undefined;
  const offers = getOffers(quantityType);
  return offers.reduce<BuyXGetYOfferSnapshot | undefined>((best, offer) => {
    const buyQty = Number(offer?.buyQty);
    const freeQty = Number(offer?.freeQty);
    if (!Number.isSafeInteger(buyQty) || !Number.isSafeInteger(freeQty) ||
      buyQty < 1 || freeQty < 1) return best;
    if (!ignoreValidity && !isOfferWithinDates(offer, atDate)) return best;
    const free = offer.isRepeatable === false
      ? (qty >= buyQty ? freeQty : 0)
      : Math.floor(qty / buyQty) * freeQty;
    const bestFree = best
      ? (best.isRepeatable === false
        ? (qty >= Number(best.buyQty) ? Number(best.freeQty) : 0)
        : Math.floor(qty / Number(best.buyQty)) * Number(best.freeQty))
      : 0;
    return free > bestFree ||
      (free === bestFree && free > 0 && buyQty > Number(best?.buyQty))
      ? offer
      : best;
  }, undefined);
};

export const getMaxPaidQuantityForPhysicalStock = (
  quantityType: QuantityTypeWithOffer | undefined,
  physicalStock: number,
  atDate: Date = new Date(),
  ignoreValidity = false
): number => {
  const available = Math.max(0, Math.floor(Number(physicalStock) || 0));
  let low = 0;
  let high = available;
  while (low < high) {
    const middle = Math.ceil((low + high) / 2);
    const dispatched = middle + getFreeQuantity(quantityType, middle, atDate, ignoreValidity);
    if (dispatched <= available) low = middle;
    else high = middle - 1;
  }
  return low;
};

export const getOfferLabel = (
  quantityType?: QuantityTypeWithOffer,
  paidQty?: number | string,
  atDate: Date = new Date(),
  ignoreValidity = false
): string => {
  const offer = paidQty === undefined
    ? quantityType?.buyXGetYOffers?.[0] || quantityType?.buyXGetY
    : getApplicableOffer(quantityType, paidQty, atDate, ignoreValidity);
  if (!quantityType?.buyXGetYEnabled || !offer?.buyQty || !offer?.freeQty) {
    return '';
  }
  return `Buy ${offer.buyQty} Get ${offer.freeQty} Free`;
};

/** Offers a customer can get today, for the order entry list. */
export const getEligibleOffers = (
  quantityType?: QuantityTypeWithOffer,
  atDate: Date = new Date()
): BuyXGetYOfferSnapshot[] => getOffers(quantityType).filter((offer) => isOfferWithinDates(offer, atDate));

export const isLoadingBayManager = (user?: UserForFreeQuantity | null): boolean =>
  (user?.role || []).some((role) => Number(role.type) === LoadingBayManager);

/** AC4: Admin, Finance Manager and Warehouse Manager see paid vs free; everyone else sees totals only. */
export const canViewFreeQuantity = (user?: UserForFreeQuantity | null): boolean => {
  if (!user) return false;
  if (isLoadingBayManager(user)) return false;
  if (
    Number(user.userType) === Admin ||
    user.roleAndPermission?.name === 'Admin'
  ) {
    return true;
  }
  if (user.roleAndPermission?.isWarehouseManagerRole) return true;
  return (user.role || []).some((r) => Number(r.type) === FinanceManager);
};
